import React from 'react';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';

interface CategoryHeaderTypographySelectorProps {
  fontWeight: number;
  fontSize: number;
  fontStyle: 'normal' | 'italic';
  onFontWeightChange: (weight: number) => void;
  onFontSizeChange: (size: number) => void;
  onFontStyleChange: (style: 'normal' | 'italic') => void;
}

/**
 * CategoryHeaderTypographySelector - Complete typography controls for category headers
 */
const CategoryHeaderTypographySelector: React.FC<CategoryHeaderTypographySelectorProps> = ({
  fontWeight,
  fontSize,
  fontStyle,
  onFontWeightChange,
  onFontSizeChange,
  onFontStyleChange
}) => {
  const handleFontWeightChange = (values: number[]) => {
    onFontWeightChange(values[0]);
  };

  const handleFontSizeChange = (values: number[]) => {
    onFontSizeChange(values[0]);
  };

  const getFontWeightLabel = (weight: number) => {
    if (weight <= 300) return 'Light';
    if (weight <= 400) return 'Normal';
    if (weight <= 500) return 'Medium';
    if (weight <= 600) return 'Semi Bold';
    if (weight <= 700) return 'Bold';
    if (weight <= 800) return 'Extra Bold';
    return 'Heavy';
  };

  const getFontSizeLabel = (size: number) => {
    if (size <= 18) return 'Small';
    if (size <= 24) return 'Medium';
    if (size <= 32) return 'Large';
    return 'Extra Large';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Typography Controls</Label>
        <div className="text-xs text-gray-500">
          {getFontWeightLabel(fontWeight)} • {getFontSizeLabel(fontSize)} • {fontStyle === 'italic' ? 'Italic' : 'Normal'}
        </div>
      </div>
      
      {/* Font Weight */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm">Font Weight</Label>
          <div className="text-xs text-gray-500">
            {getFontWeightLabel(fontWeight)} ({fontWeight})
          </div>
        </div>
        
        <div className="space-y-2">
          <Slider
            value={[fontWeight]}
            onValueChange={handleFontWeightChange}
            min={100}
            max={900}
            step={100}
            className="w-full"
          />
          
          <div className="flex justify-between text-xs text-gray-400">
            <span>Light (100)</span>
            <span>Normal (400)</span>
            <span>Bold (700)</span>
            <span>Heavy (900)</span>
          </div>
        </div>
      </div>

      {/* Font Size */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm">Font Size</Label>
          <div className="text-xs text-gray-500">
            {getFontSizeLabel(fontSize)} ({fontSize}px)
          </div>
        </div>
        
        <div className="space-y-2">
          <Slider
            value={[fontSize]}
            onValueChange={handleFontSizeChange}
            min={14}
            max={48}
            step={2}
            className="w-full"
          />
          
          <div className="flex justify-between text-xs text-gray-400">
            <span>Small (14px)</span>
            <span>Medium (24px)</span>
            <span>Large (32px)</span>
            <span>XL (48px)</span>
          </div>
        </div>
      </div>

      {/* Font Style */}
      <div className="space-y-3">
        <Label className="text-sm">Font Style</Label>
        
        <ToggleGroup
          type="single"
          value={fontStyle}
          onValueChange={(value) => {
            if (value) onFontStyleChange(value as 'normal' | 'italic');
          }}
          className="justify-start"
        >
          <ToggleGroupItem value="normal" aria-label="Normal text">
            <span className="font-normal">Normal</span>
          </ToggleGroupItem>
          <ToggleGroupItem value="italic" aria-label="Italic text">
            <span className="italic">Italic</span>
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      {/* Quick Presets */}
      <div className="space-y-3">
        <Label className="text-sm">Quick Presets</Label>
        
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              onFontWeightChange(400);
              onFontSizeChange(24);
              onFontStyleChange('normal');
            }}
            className="justify-start"
          >
            Regular
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              onFontWeightChange(700);
              onFontSizeChange(28);
              onFontStyleChange('normal');
            }}
            className="justify-start"
          >
            Bold
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              onFontWeightChange(800);
              onFontSizeChange(32);
              onFontStyleChange('normal');
            }}
            className="justify-start"
          >
            Large Bold
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              onFontWeightChange(600);
              onFontSizeChange(26);
              onFontStyleChange('italic');
            }}
            className="justify-start"
          >
            Elegant
          </Button>
        </div>
      </div>
      
      <div className="text-xs text-gray-500">
        Control font weight (100-900), size (14-48px), and style for category headers
      </div>
    </div>
  );
};

export default CategoryHeaderTypographySelector;